import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import classNames from 'classnames';
import fileSize from 'filesize';
import PropTypes from 'prop-types';
import { getShortenedNumberComponents, millisecondToHumanReadableTime } from '@quarry/utils';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".unit {\n  margin-left: 4px; }\n\n.stat {\n  font-size: 16px;\n  line-height: 24px; }\n  .stat.lg .value {\n    font-size: 32px;\n    font-weight: 200;\n    line-height: 32px; }\n  .stat.xl {\n    height: 90px;\n    line-height: initial; }\n    .stat.xl .unit {\n      margin-left: 0.5px; }\n    .stat.xl .value {\n      font-size: 72px;\n      font-weight: 200;\n      line-height: 72px; }\n";
styleInject(css_248z);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var timeUnits = defineMessages({
  millisecond: {
    id: 'stat.human-readable-time.milliseconds',
    defaultMessage: '{value, plural, one {millisecond} other {milliseconds}}'
  },
  second: {
    id: 'stat.human-readable-time.seconds',
    defaultMessage: '{value, plural, one {second} other {seconds}}'
  },
  minute: {
    id: 'stat.human-readable-time.minutes',
    defaultMessage: '{value, plural, one {minute} other {minutes}}'
  },
  hour: {
    id: 'stat.human-readable-time.hours',
    defaultMessage: '{value, plural, one {hour} other {hours}}'
  },
  day: {
    id: 'stat.human-readable-time.days',
    defaultMessage: '{value, plural, one {day} other {days}}'
  }
});
var StatSize = {
  MEDIUM: 'md',
  LARGE: 'lg',
  XL: 'xl'
};
function Stat(_ref) {
  var size = _ref.size,
      unit = _ref.unit,
      value = _ref.value;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames("stat", size)
  }, /*#__PURE__*/React.createElement("span", {
    className: "value"
  }, value), /*#__PURE__*/React.createElement("span", {
    className: "unit"
  }, unit));
}
Stat.defaultProps = {
  size: StatSize.MEDIUM
};
Stat.propTypes = {
  size: PropTypes.oneOf(Object.values(StatSize)),
  unit: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
function ShortNumericStat(_ref2) {
  var intl = _ref2.intl,
      originalValue = _ref2.value,
      otherProps = _objectWithoutProperties(_ref2, ["intl", "value"]);

  var _getShortenedNumberCo = getShortenedNumberComponents(intl, originalValue),
      unit = _getShortenedNumberCo.unit,
      value = _getShortenedNumberCo.value;

  return /*#__PURE__*/React.createElement(Stat, _extends({}, otherProps, {
    unit: unit,
    value: value
  }));
}
var ShortenedNumericStat = injectIntl(ShortNumericStat);
ShortNumericStat.defaultProps = _objectSpread({}, Stat.defaultProps);
ShortNumericStat.propTypes = _objectSpread(_objectSpread({}, Stat.propTypes), {}, {
  intl: PropTypes.object.isRequired,
  value: PropTypes.number.isRequired
});
function FormatNumericStat(_ref3) {
  var intl = _ref3.intl,
      originalValue = _ref3.value,
      otherProps = _objectWithoutProperties(_ref3, ["intl", "value"]);

  var value = intl.formatNumber(originalValue, _objectSpread({}, otherProps));
  return /*#__PURE__*/React.createElement(Stat, _extends({}, otherProps, {
    value: value
  }));
}
var NumericStat = injectIntl(FormatNumericStat);
FormatNumericStat.defaultProps = _objectSpread({}, Stat.defaultProps);
FormatNumericStat.propTypes = _objectSpread(_objectSpread({}, Stat.propTypes), {}, {
  intl: PropTypes.object.isRequired,
  value: PropTypes.number.isRequired
});
function ByteStat(_ref4) {
  var originalValue = _ref4.value,
      otherProps = _objectWithoutProperties(_ref4, ["value"]);

  var _fileSize = fileSize(originalValue, _objectSpread(_objectSpread({}, otherProps), {}, {
    output: 'object'
  })),
      value = _fileSize.value,
      suffix = _fileSize.suffix;

  return /*#__PURE__*/React.createElement(NumericStat, _extends({}, otherProps, {
    value: value,
    unit: suffix
  }));
}
ByteStat.defaultProps = _objectSpread({}, Stat.defaultProps);
ByteStat.propTypes = _objectSpread(_objectSpread({}, Stat.propTypes), {}, {
  value: PropTypes.number.isRequired
});
function FormatTimeStat(_ref5) {
  var intl = _ref5.intl,
      originalValue = _ref5.value,
      otherProps = _objectWithoutProperties(_ref5, ["intl", "value"]);

  var _millisecondToHumanRe = millisecondToHumanReadableTime(originalValue),
      value = _millisecondToHumanRe.value,
      unit = _millisecondToHumanRe.unit;

  var unitMessage = timeUnits[unit];
  return /*#__PURE__*/React.createElement(NumericStat, _extends({}, otherProps, {
    value: value,
    unit: unitMessage ? intl.formatMessage(unitMessage, {
      value: value
    }) : unit
  }));
}
var TimeStat = injectIntl(FormatTimeStat);
FormatTimeStat.defaultProps = _objectSpread({}, Stat.defaultProps);
FormatTimeStat.propTypes = _objectSpread(_objectSpread({}, Stat.propTypes), {}, {
  value: PropTypes.number.isRequired
});

export { ByteStat, FormatNumericStat, FormatTimeStat, NumericStat, ShortNumericStat, ShortenedNumericStat, Stat, StatSize, TimeStat };
